import { useDispatch, useSelector } from "react-redux";
import Typography from "../../components/Typography";
import { customTokens } from "../../theme/theme";
import { useChatWindowStyles } from "./AIClimateAssistant.styles";
import PromptsWindow from "./PromptsWindow";
import { RootState } from "../../store";
import {
  ArrowClockwise20Regular,
  ArrowDownload20Regular,
  Copy20Regular,
  Edit20Regular,
  Sparkle20Regular,
} from "@fluentui/react-icons";
import logoSmall from "../../assets/images/UNFCCC small logo.svg";
import {
  FC,
  Fragment,
  memo,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
} from "react";
import { IconButton } from "../../components/IconButton";
import AiStackedBarChart from "../../components/AiGraphs/AiStackedBarChart";
import AiPieChart from "../../components/AiGraphs/AiPieChart";
import AiBiaxialChart from "../../components/AiGraphs/AiBiaxialChart";
import AiAreaChart from "../../components/AiGraphs/AiAreaChart";
import AiLineChart from "../../components/AiGraphs/AiLineChart";
import AiTreemapChart from "../../components/AiGraphs/AiTreemapChart";
import AiSankeyChart from "../../components/AiGraphs/AiSankeyChart";
import AiRadarChart from "../../components/AiGraphs/AiRadarChart";
import AiTwoLevelPieChart from "../../components/AiGraphs/AiTwoLevelPieChart";
import AiScatterChart from "../../components/AiGraphs/AiScatterChart";
import AiStackedAreaChart from "../../components/AiGraphs/AiStackedAreaChart";
import AiBarChart from "../../components/AiGraphs/AiBarChart";
import { IAiChart } from "../../components/AiGraphs/chartConfig";
import { Button, Link, ProgressBar } from "@fluentui/react-components";
import { exportChart } from "../../utils/exportChart";
import { generateColumns } from "./AIClimateAssistant.config";
import { DataGrid } from "../../components/DataGrid";
import { downloadBase64File } from "../../utils/fileExporter";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import {
  setQuery,
  setResetQuery,
} from "../../store/slices/ClimateAssistantSlice";
import { useIsMobile } from "../../hooks";

interface IResponseBubbleWrapper {
  children: ReactNode;
  tagName?: string;
}

const ResponseBubbleWrapper: FC<IResponseBubbleWrapper> = ({
  tagName = "AI-powered conversation",
  children,
}) => {
  const styles = useChatWindowStyles();

  return (
    <>
      <div className={styles.responseContainer}>
        <img src={logoSmall} alt="UNFCCC logo" className={styles.avatar} />

        <div className={styles.responseWrapper}>
          <Sparkle20Regular />
          <Typography
            variant="bodyXXRegular"
            color={customTokens.colorBrandBackground}
          >
            {tagName}
          </Typography>
        </div>
      </div>

      <div className={styles.responseChild}>{children}</div>
    </>
  );
};

const chartComponentMap: Record<string, React.FC<IAiChart>> = {
  bar: AiBarChart,
  sankey: AiSankeyChart,
  radar: AiRadarChart,
  two_level_pie: AiTwoLevelPieChart,
  scatter: AiScatterChart,
  stacked_area: AiStackedAreaChart,
  stacked_bar: AiStackedBarChart,
  domainradar: AiRadarChart,
  pie: AiPieChart,
  biaxial_bar: AiBiaxialChart,
  area: AiAreaChart,
  percent_area: AiAreaChart,
  line: AiLineChart,
  treemap: AiTreemapChart,
};

const newSessionTexts = {
  newSessionTitle: "How can I help you today?",
  newSessionDesc: "Maybe you would like to start with a query like these?",
  expiredSessionTitle:
    "Sorry, you've run out of questions for this topic. Would you like to start a new topic?",
  expiredSessionDesc:
    "You can begin with the following suggestions, or type your own.",
  newTopicTitle: "Would you like to start a new topic?",
  newTopicDesc:
    "You can begin with the following suggestions or by typing your own.",
};

interface IChatWindow {
  containerRef: RefObject<HTMLDivElement>;
  handleSend: (customQuery?: string, topicName?: string) => Promise<void>;
  handleScroll: VoidFunction;
}

const ChatWindow: FC<IChatWindow> = ({
  containerRef,
  handleSend,
  handleScroll,
}) => {
  const styles = useChatWindowStyles();
  const chartRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const {
    resetQuery,
    initialLoad,
    message,
    loading,
    togglePromptOverlay,
    topicName,
  } = useSelector((state: RootState) => state.climateAssistant);

  useEffect(() => {
    handleScroll();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [togglePromptOverlay]);

  const handleExport = () => {
    if (chartRef.current) {
      exportChart(chartRef.current);
    }
  };

  const handleEditClick = (text: string) => {
    dispatch(setQuery(text));
    dispatch(setResetQuery(text));
  };

  const userQueryBubble = (showText: boolean, query: string) => {
    if (showText) {
      return (
        <div className={styles.userQueryBubbleWrapper}>
          <Edit20Regular
            className={styles.userQueryEditBtn}
            onClick={() => handleEditClick(query)}
          />

          <div className={styles.userQueryBubble}>
            <Typography
              color={customTokens.colorNeutralForeground2}
              variant="bodyMRegular"
              mobileVariant="body1"
            >
              {query}
            </Typography>
          </div>
        </div>
      );
    }

    return null;
  };

  const newSessionBubble = (title: string, description: string) => {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="bodyMRegular"
          mobileVariant="body1"
          color={customTokens.colorNeutralForeground2}
        >
          {title}
        </Typography>

        <Typography
          variant="bodyMRegular"
          mobileVariant="body1"
          color={customTokens.colorNeutralForeground2}
        >
          {description}
        </Typography>

        <PromptsWindow handleSend={handleSend} />
      </div>
    );
  };

  const promptOverlayBubble = () => {
    return (
      <div
        style={{ display: "flex", flexDirection: "column", padding: "0 42px" }}
      >
        <Typography
          variant="bodyLSemibold"
          color={customTokens.colorNeutralForeground2}
        >
          Would you like to know more on Climate Data?
        </Typography>

        <Typography
          variant="bodyMRegular"
          mobileVariant="body1"
          color={customTokens.colorNeutralForeground2}
        >
          Learn more by using our curated topics
        </Typography>

        <PromptsWindow handleSend={handleSend} />
      </div>
    );
  };

  const loadingBubble = () => {
    return (
      <div>
        <Typography
          variant="bodyMRegular"
          mobileVariant="body1"
          color={customTokens.colorBrandBackground}
        >
          OK, I'll research this topic and provide a suitable answer...
        </Typography>

        <ProgressBar style={{ marginTop: "16px" }} />
      </div>
    );
  };

  const errorBubble = (showError: boolean = false, errorText: string) => {
    if (showError) {
      return (
        <div className={styles.errorBubble}>
          <Typography
            variant="bodyMRegular"
            mobileVariant="body1"
            color={customTokens.colorStatusDangerForeground1}
          >
            {errorText}
          </Typography>
        </div>
      );
    }

    return null;
  };

  const textResponseBubble = (
    showText: boolean,
    text: string,
    requestId?: string
  ) => {
    if (showText) {
      return (
        <div className={styles.textBubble}>
          {requestId === "req001" && topicName && (
            <div className={styles.newTopicWrapper}>
              <div className={styles.newTopic}>
                <Typography
                  variant="bodyXXRegular"
                  color={customTokens.UNFCCCPrimaryAccent100}
                >
                  New topic
                </Typography>
              </div>

              <Typography
                variant="bodyMRegular"
                mobileVariant="body1"
                color={customTokens.UNFCCCPrimaryAccent100}
              >
                {topicName}
              </Typography>
            </div>
          )}

          <Typography
            variant="bodyMRegular"
            mobileVariant="body1"
            color={customTokens.colorNeutralForeground2}
          >
            <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
              {text}
            </ReactMarkdown>
          </Typography>
        </div>
      );
    }

    return null;
  };

  const analysisResponseBubble = (
    showText: boolean,
    text: string,
    kpiName: string = ""
  ) => {
    if (showText) {
      return (
        <div className={styles.analysisTextBubble}>
          <div className={styles.newTopicWrapper}>
            <div className={styles.newTopic}>
              <Typography
                variant="bodyXXRegular"
                color={customTokens.UNFCCCPrimaryAccent100}
              >
                New topic
              </Typography>
            </div>

            <Typography
              variant="bodyMRegular"
              mobileVariant="body1"
              color={customTokens.UNFCCCPrimaryAccent100}
            >
              {kpiName}
            </Typography>
          </div>

          <Typography
            variant="bodyMRegular"
            mobileVariant="body1"
            color={customTokens.colorNeutralForeground2}
          >
            <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
              {text}
            </ReactMarkdown>
          </Typography>
        </div>
      );
    }

    return null;
  };

  const graphResponseBubble = (graph: any, chartData: any) => {
    if (graph && graph.chart_type && chartData?.length) {
      const { chart_type, title, description } = graph;
      const ChartComponent = chartComponentMap[chart_type];

      if (ChartComponent) {
        return (
          <div className={styles.graphBubbleContainer} ref={chartRef}>
            {title && (
              <Typography
                variant="bodyMRegular"
                mobileVariant="body1"
                color={customTokens.colorNeutralForeground2}
              >
                {title}
              </Typography>
            )}

            {description && (
              <Typography
                variant="bodyMRegular"
                mobileVariant="body1"
                color={customTokens.colorNeutralForeground2}
              >
                {description}
              </Typography>
            )}

            <div className={styles.graphWrapper}>
              <ChartComponent chartData={chartData} config={graph} />
            </div>

            <Button
              appearance="primary"
              icon={<ArrowDownload20Regular />}
              className={styles.downloadGraphBtn}
              data-no-export
              onClick={() => handleExport()}
            />
          </div>
        );
      }
    }

    return null;
  };

  const tableResponseBubble = (hasTable: boolean, tableData: any) => {
    if (hasTable) {
      const columnConfig = generateColumns(tableData);

      return (
        <div className={styles.tableBubble}>
          <DataGrid items={tableData} columnConfig={columnConfig} />
        </div>
      );
    }

    return null;
  };

  const csvDownloadBubble = (hasCSV: boolean, csvData: string) => {
    if (hasCSV) {
      return (
        <div className={styles.csvBubble}>
          <Typography
            variant="bodyMRegular"
            mobileVariant="body1"
            color={customTokens.colorNeutralForeground2}
          >
            Due to the large size of the table data, it is not displayed here.
            Please download the CSV file to view the complete dataset.
          </Typography>{" "}
          <Link
            onClick={() =>
              downloadBase64File(csvData, "csv", "climate-data-export.csv")
            }
          >
            <Typography
              variant="bodyMRegular"
              mobileVariant="body1"
              color={customTokens.colorBrandForegroundLink}
            >
              climate-data-export.csv
            </Typography>
          </Link>
        </div>
      );
    }

    return null;
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className={isMobile ? styles.mobileScrollWindow : styles.scrollWindow}>
      {togglePromptOverlay ? (
        <div className={styles.chatWindow}>{promptOverlayBubble()}</div>
      ) : (
        <div className={styles.chatWindow}>
          {initialLoad && (
            <ResponseBubbleWrapper>
              {newSessionBubble(
                newSessionTexts.newSessionTitle,
                newSessionTexts.newSessionDesc
              )}
            </ResponseBubbleWrapper>
          )}

          {message.map((msg, index) => {
            const {
              sender,
              hasError,
              showNewSession,
              sessionExpired,
              isAiAnalysis,
              kpiName,
              requestId,
              data: { text, graph, graphSQLData, table, tableSQLData, csv },
            } = msg;

            const isUser = sender === "user";

            const hasText = !hasError && !!text;
            const hasTable = table?.show_table;
            const hasCSV = table?.show_csv;

            return (
              <Fragment key={`${sender}-${index}`}>
                <div ref={containerRef}></div>

                {isUser ? (
                  <>{userQueryBubble(hasText, text)}</>
                ) : (
                  <>
                    <ResponseBubbleWrapper
                      tagName={
                        isAiAnalysis
                          ? "AI-powered analysis"
                          : "AI-powered conversation"
                      }
                    >
                      {errorBubble(hasError, text)}
                      {isAiAnalysis
                        ? analysisResponseBubble(hasText, text, kpiName)
                        : textResponseBubble(hasText, text, requestId)}
                      {graphResponseBubble(graph, graphSQLData)}
                      {tableResponseBubble(hasTable, tableSQLData)}
                      {csvDownloadBubble(hasCSV, csv)}
                      {showNewSession &&
                        newSessionBubble(
                          newSessionTexts.newTopicTitle,
                          newSessionTexts.newTopicDesc
                        )}
                    </ResponseBubbleWrapper>

                    {sessionExpired && (
                      <div style={{ marginTop: "24px" }}>
                        <ResponseBubbleWrapper>
                          {newSessionBubble(
                            newSessionTexts.expiredSessionTitle,
                            newSessionTexts.expiredSessionDesc
                          )}
                        </ResponseBubbleWrapper>
                      </div>
                    )}

                    {!showNewSession && (
                      <div style={{ margin: "16px auto 16px 42px" }}>
                        <IconButton
                          icon={<Copy20Regular />}
                          ariaLabel="copy"
                          onClick={() => handleCopy(text)}
                        />

                        {!isAiAnalysis && (
                          <IconButton
                            icon={<ArrowClockwise20Regular />}
                            ariaLabel="regenerate"
                            onClick={() => handleSend(resetQuery)}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </Fragment>
            );
          })}

          {loading && (
            <ResponseBubbleWrapper>{loadingBubble()}</ResponseBubbleWrapper>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(ChatWindow);
